import { Column, Loader, SearchField, SearchSuggestion, Text } from '@amzn/meridian';
import { type PapiSearchFieldPapiUserFragment, usePapiSearchQuery } from 'generated/graphql';
import { type FC, useState } from 'react';
import { useDebounce } from 'react-use';
import { PAPISearchFieldSuggestion } from './PAPISearchFieldSuggestion';

const MinimumQueryLength = 3;

export type PAPISearchFieldProps = {
  onSelectPerson: (person: PapiSearchFieldPapiUserFragment) => void;
};

export const PAPISearchField: FC<PAPISearchFieldProps> = ({ onSelectPerson }) => {
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const { loading, error, data } = usePapiSearchQuery({
    fetchPolicy: 'network-only',
    skip: query.length < MinimumQueryLength,
    variables: {
      query,
    },
  });

  useDebounce(
    () => {
      setQuery(searchInput);
    },
    300,
    [
      searchInput,
    ],
  );

  const userSuggestions =
    data?.getPapiUsers.map((user) => ({
      ...user,
      login: user.login.toLowerCase(),
    })) ?? [];

  const onSearchFieldSubmit = (query: string) => {
    if (query.length < MinimumQueryLength) return;

    const foundSuggestion = userSuggestions.find((suggestion) => suggestion.login === query);
    if (foundSuggestion) {
      onSelectPerson(foundSuggestion);
      setSearchInput('');
    }
  };

  const onSuggestionClicked = (user: PapiSearchFieldPapiUserFragment) => {
    onSelectPerson(user);
    setSearchInput('');
  };

  return (
    <SearchField
      clearButton
      data-cy="papi-search-field"
      onChange={setSearchInput}
      onSubmit={onSearchFieldSubmit}
      placeholder="Search for and add users to this project"
      popoverMaxHeight={350}
      type="text"
      value={searchInput}
    >
      {loading ? (
        <Column alignmentHorizontal="center" spacing="none" spacingInset="100">
          <Text>Searching...</Text>
          <Loader data-cy="papi-search-loader" size="medium"></Loader>
        </Column>
      ) : null}

      {!loading && error ? (
        <Column spacing="none" spacingInset="100">
          <Text alignment="center">Something went wrong while fetching users.</Text>
        </Column>
      ) : null}

      {!loading && !error && userSuggestions.length === 0 && searchInput.length > MinimumQueryLength ? (
        <Column spacing="none" spacingInset="100">
          <Text alignment="center">No users found.</Text>
        </Column>
      ) : null}

      {userSuggestions.map((user) => (
        <SearchSuggestion key={user.login} onClick={() => onSuggestionClicked(user)}>
          {({ query = '' }) => <PAPISearchFieldSuggestion query={query} userData={user} />}
        </SearchSuggestion>
      ))}
    </SearchField>
  );
};
